import {
  TECHNICAL_SPEC_TEMPLATE_V1,
  type TechnicalSpecificationSection,
  type TechnicalSpecificationState
} from '../../services/projectAutofillDraftService.ts'
import { buildBudgetBinding, type BudgetSheetRubroRef } from './budgetSheetStateUtils.ts'

export const MAX_TECHNICAL_SPEC_SECTIONS = 12
export const MAX_TECHNICAL_SPEC_TITLE_CHARS = 120
export const MAX_TECHNICAL_SPEC_BODY_CHARS = 8000

export const DEFAULT_TECHNICAL_SPEC_SECTIONS: { id: string; title: string }[] = [
  { id: 'alcance', title: 'Alcance del rubro' },
  { id: 'materiales', title: 'Materiales y equipos' },
  { id: 'ejecucion', title: 'Modo de ejecución' },
  { id: 'normas', title: 'Normas y criterios de calidad' }
]

function newSectionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `sec-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function trimBody(raw: string): string {
  return raw.length > MAX_TECHNICAL_SPEC_BODY_CHARS
    ? raw.slice(0, MAX_TECHNICAL_SPEC_BODY_CHARS)
    : raw
}

function trimTitle(raw: string): string {
  const t = raw.trim()
  return t.length > MAX_TECHNICAL_SPEC_TITLE_CHARS ? t.slice(0, MAX_TECHNICAL_SPEC_TITLE_CHARS) : t
}

export function buildDefaultTechnicalSpecState(rubro: BudgetSheetRubroRef): TechnicalSpecificationState {
  return {
    templateVersion: TECHNICAL_SPEC_TEMPLATE_V1,
    binding: buildBudgetBinding(rubro),
    sections: DEFAULT_TECHNICAL_SPEC_SECTIONS.map((s) => ({
      id: s.id,
      title: s.title,
      body: ''
    })),
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function attachTechnicalSpecBindingToState(
  rubro: BudgetSheetRubroRef,
  state: TechnicalSpecificationState
): TechnicalSpecificationState {
  return {
    ...state,
    binding: buildBudgetBinding(rubro)
  }
}

export function applyTechnicalSpecSectionBodyChange(
  state: TechnicalSpecificationState,
  sectionId: string,
  body: string
): TechnicalSpecificationState {
  return {
    ...state,
    sections: state.sections.map((s) =>
      s.id === sectionId ? { ...s, body: trimBody(body) } : s
    ),
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function applyTechnicalSpecSectionTitleChange(
  state: TechnicalSpecificationState,
  sectionId: string,
  title: string
): TechnicalSpecificationState {
  return {
    ...state,
    sections: state.sections.map((s) =>
      s.id === sectionId ? { ...s, title: trimTitle(title) || s.title } : s
    ),
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function addTechnicalSpecSection(state: TechnicalSpecificationState): TechnicalSpecificationState {
  if (state.sections.length >= MAX_TECHNICAL_SPEC_SECTIONS) return state
  const section: TechnicalSpecificationSection = {
    id: newSectionId(),
    title: 'Nueva sección',
    body: ''
  }
  return {
    ...state,
    sections: [...state.sections, section],
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function removeTechnicalSpecSection(
  state: TechnicalSpecificationState,
  sectionId: string
): TechnicalSpecificationState {
  if (state.sections.length <= 1) return state
  return {
    ...state,
    sections: state.sections.filter((s) => s.id !== sectionId),
    source: 'user_edited',
    updatedAt: new Date().toISOString()
  }
}

export function validateTechnicalSpecStateClient(state: TechnicalSpecificationState): string | null {
  if (state.templateVersion !== TECHNICAL_SPEC_TEMPLATE_V1) {
    return 'Versión de plantilla de especificaciones técnicas no soportada'
  }
  if (!state.binding?.tempId?.trim()) return 'binding.tempId requerido'
  if (!state.binding?.rubroLabel?.trim()) return 'binding.rubroLabel requerido'
  if (!state.sections.length) return 'Agregá al menos una sección'
  const ids = new Set<string>()
  for (const s of state.sections) {
    if (!s.id?.trim()) return 'Cada sección necesita un identificador'
    if (ids.has(s.id)) return 'Hay secciones con el mismo identificador'
    ids.add(s.id)
  }
  const hasBody = state.sections.some((s) => String(s.body ?? '').trim().length > 0)
  if (!hasBody) return 'Completá al menos una sección con texto'
  return null
}

export function serializeTechnicalSpecForDirtySnapshot(
  state: TechnicalSpecificationState | undefined
): string {
  if (!state) return ''
  return JSON.stringify({
    templateVersion: state.templateVersion,
    source: state.source,
    sections: state.sections.map((s) => ({ id: s.id, title: s.title, body: s.body }))
  })
}
